<template>
  <section
    class="box has-border-light"
    :class="{ 'has-background-secondary': billingPlan.isRecommended }"
    :data-e2e="BillingCypressAttribute.billingPlans.views.CARD_BOX"
  >
    <span
      v-if="billingPlan.isRecommended"
      class="tag is-dark has-text-weight-semibold floating-tag is-size-5 px-3 py-1"
      :data-e2e="BillingCypressAttribute.billingPlans.views.MOST_POPULAR_TAG"
    >
      {{ $t('solutions-and-pricing.most-popular') }}
    </span>
    <div class="p-5">
      <div class="is-flex is-flex-direction-column">
        <div class="is-flex is-align-items-center is-justify-content-space-between">
          <div class="p-r-5 has-text-left">
            <p
              class="has-text-weight-semibold is-size-1 has-text-primary"
              :data-e2e="LightDiagnosticCypressAttribute.views.BILLING_PLAN_TITLE"
            >
              {{ billingPlan.name }}
            </p>
            <p class="has-text-weight-medium has-text-grey is-size-4">{{ billingPlan.subTitle }}</p>
          </div>
          <TooltipView v-if="informationTooltip" type="is-dark" multilined>
            <template #content>
              <p class="p-1" v-dompurify-html="informationTooltip" />
            </template>
            <IconView
              pack="cls"
              size="is-small"
              custom-class="cls-information-circle has-text-grey"
            />
          </TooltipView>
        </div>
        <div class="is-flex-grow-1" />
        <div class="has-text-left">
          <div v-if="isAvailable">
            <slot name="custom-pricing" :billing-plan="billingPlan">
              <p
                class="has-text-weight-semibold is-size-1"
                :data-e2e="EstateCypressAttribute.standardPlan.views.PRICE"
              >
                {{ billingPlan.formattedFinalPrice }}
              </p>
              <p
                v-if="billingPlan.chargeRecurringFee"
                class="is-size-6"
                :data-e2e="EstateCypressAttribute.standardPlan.views.YEARLY_PRICE"
              >
                +{{ $t('standardplan.per-year', { price: billingPlan.formattedYearlyPrice }) }}
              </p>
            </slot>
          </div>
          <Banner
            v-else
            class="py-2"
            :title="$t('billingplan.product-not-yet-available')"
            type="is-warning"
            title-text-size="is-size-5"
          />
        </div>
      </div>
      <slot name="actions" :billing-plan="billingPlan" />
      <ButtonView
        v-if="hasCallToAction"
        type="is-inverted"
        class="has-text-weight-semibold m-t-20 is-full-width has-text-primary has-border-primary"
        outlined
        @click="onClick"
        :data-e2e="EstateCypressAttribute.standardPlan.buttons.SELECT_NON_STANDARD_PLAN"
      >
        {{ billingPlan.upsellActionText }}
      </ButtonView>
      <p class="has-text-left m-b-20 m-t-20 is-size-5">
        {{ billingPlan.upsellDescription }}
      </p>
      <p class="has-text-left has-text-weight-semibold is-size-4 m-b-5">
        {{ billingPlan.upsellTitle }}
      </p>
      <slot name="inclusions-exclusions" :billing-plan="billingPlan" />
    </div>
  </section>
</template>

<script>
import { EstateCypressAttribute } from '/@/modules/estate/constants/EstateCypressAttribute';
import { LightDiagnosticCypressAttribute } from '@/modules/lightdiagnostic/constants/LightDiagnosticCypressAttribute';
import { SelfServeBillingPlanForm } from '@/modules/estate/models/SelfServeBillingPlanForm';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import { BillingCypressAttribute } from '@/modules/billing/constants/BillingCypressAttribute';
import Banner from '@/modules/core/ui/components/Banner.vue';
import TooltipView from '@/modules/core/ui/components/wrappers/TooltipView.vue';
import IconView from '@/modules/core/ui/components/wrappers/IconView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'VaultCompactPlanBillingCard',
  emits: ['on-plan-clicked'],
  slotsDefinition: {
    customPricing: { billingPlan: SelfServeBillingPlanForm },
    actions: { billingPlan: SelfServeBillingPlanForm },
    inclusionsExclusions: { billingPlan: SelfServeBillingPlanForm }
  },
  components: { ButtonView, Banner, TooltipView, IconView },
  props: {
    billingPlan: {
      type: SelfServeBillingPlanForm,
      required: true
    },
    selectedJurisdiction: {
      type: Number,
      default: null
    },
    hasCallToAction: {
      type: Boolean,
      default: true
    },
    informationTooltip: {
      type: String,
      required: false
    }
  },
  static: {
    EstateCypressAttribute,
    LightDiagnosticCypressAttribute,
    BillingCypressAttribute
  },
  computed: {
    isAvailable() {
      return this.billingPlan.isAvailableForJurisdiction(this.selectedJurisdiction);
    }
  },
  methods: {
    onClick() {
      this.$emit('on-plan-clicked', this.billingPlan);
    }
  }
};
</script>

<style scoped>
@media (max-width: 1023px) {
  section.box {
    width: 100% !important;
  }
}

section.box {
  padding: var(--ce-3px) !important;
  height: 100%;
  position: relative;
}

:deep(.tooltip-content) {
  width: 75% !important;
}

.tag {
  background-color: var(--ce-primary) !important;
  color: var(--ce-white) !important;
  text-indent: 0;
  padding: 0 var(--ce-4px);
  height: unset;
}

.floating-tag {
  position: absolute;
  top: var(--ce--16px);
  left: var(--ce-16px);
  white-space: nowrap;
}

:deep(.tooltip-content) {
  min-width: var(--ce-350px);
}

:deep(.button.is-inverted) {
  color: var(--ce-primary) !important;
}
</style>
